import { lazy, type ComponentType, type LazyExoticComponent } from "react";

import {
  areAdapterQuestionsAnswered,
  getApplicableStepBlocks,
  getEntitySelectionQuestions,
  getExternalConfigConfirmationId,
  getStepResources,
  getVerificationItems,
  matchesAdapterAnswers,
  type AdapterApplyAnswers,
  type AdapterQuestion,
  type AdapterStepBlock,
  type AdapterStepBlockKind,
  type AdapterWorkflowStepDefinition,
  type ResourceInspection,
} from "@/features/adapters/model";
import type {
  AdapterBlockCompletionContext,
  AdapterBlockProps,
  AdapterBlockStatus,
  AdapterBlockRegistration,
  AdapterStepCompletionContext,
  AdapterStepEvaluationContext,
  AdapterWizardStep,
} from "@/features/adapters/stepTypes";
import { getAdapterFileProcessingDetails } from "@/features/adapters/fileProcessing";
import { ConnectionStepHeader, ConnectorStepHeader } from "@/features/adapters/components/AdapterStepHeaders";
import { ContentBlock, DividerBlock, ExternalConfigBlock } from "@/features/adapters/components/blocks/InformationBlocks";

/**
 * Several statuses as one: any error or warning wins; otherwise success when all succeed, not started when none
 * has, and in progress in between. Untracked (undefined) statuses are ignored; nothing tracked is a success.
 */
export const combineAdapterStatuses = (statuses: (AdapterBlockStatus | undefined)[]): AdapterBlockStatus => {
  const tracked = statuses.filter((status): status is AdapterBlockStatus => Boolean(status));
  if (tracked.includes("destructive")) return "destructive";
  if (tracked.includes("warning")) return "warning";
  if (tracked.every((status) => status === "success")) return "success";
  if (tracked.every((status) => status === "not-started")) return "not-started";
  return "in-progress";
};

const completeWhen = (complete: boolean): AdapterBlockStatus => (complete ? "success" : "not-started");

/** A list of checks: success when all are done, in progress when some are. */
const progressOf = (done: boolean[]): AdapterBlockStatus =>
  combineAdapterStatuses(done.map(completeWhen));

/**
 * A resource-backed block's status, from the applicable resources of its own kinds on the step. A resource
 * that exists but isn't set up right is a warning; one that doesn't exist yet is not started.
 */
const resourceBlockStatus = ({ step, block, inspections }: {
  step: { key: string };
  block: object;
  inspections: ResourceInspection[];
}): AdapterBlockStatus => {
  const owned = blockInspections({ step, block, inspections });
  if (owned.length === 0) return "not-started";
  return combineAdapterStatuses(owned.map((inspection): AdapterBlockStatus =>
    inspection.complete ? "success" : inspection.state === "error" ? "destructive" : inspection.state === "incomplete" ? "warning" : "not-started",
  ));
};

/** The applicable resources of the block's own kinds on the step. */
const blockInspections = ({ step, block, inspections }: {
  step: { key: string };
  block: object;
  inspections: ResourceInspection[];
}): ResourceInspection[] => {
  const kinds = new Set(((block as { resources?: { kind: string }[] }).resources ?? []).map((resource) => resource.kind));
  return inspections.filter(
    (inspection) => inspection.stepId === step.key && inspection.applicable && (kinds.size === 0 || kinds.has(inspection.kind)),
  );
};

const areBlockQuestionsAnswered = ({ block, answers }: { block: { questions: AdapterQuestion[] }; answers: AdapterApplyAnswers }): boolean =>
  areAdapterQuestionsAnswered(block.questions.filter((question) => matchesAdapterAnswers(answers, question.showWhen)), answers);

const areVerificationItemsConfirmed = ({ definition, answers, confirmations }: AdapterStepEvaluationContext): boolean =>
  getVerificationItems(definition, answers).every((item) => confirmations[item.id]);

/**
 * The single registry of block kinds. A new block kind is a declaration merge into
 * `AdapterStepBlockDataMap`, a component, and one entry here; interactive blocks are loaded on demand so
 * only the active step's code is fetched.
 */
const registrations: { [Kind in AdapterStepBlockKind]: AdapterBlockRegistration<Kind> } = {
  content: {
    kind: "content",
    informational: true,
    requiresConfiguration: false,
    status: () => undefined,
    Component: ContentBlock,
  },
  divider: {
    kind: "divider",
    informational: true,
    requiresConfiguration: false,
    status: () => undefined,
    Component: DividerBlock,
  },
  externalConfig: {
    kind: "externalConfig",
    informational: true,
    requiresConfiguration: false,
    // Its "Done" checkbox only counts when the block is required.
    status: ({ step, block, confirmations }) =>
      block.required ? completeWhen(Boolean(confirmations[getExternalConfigConfirmationId(step.key, block.id)])) : undefined,
    Component: ExternalConfigBlock,
  },
  entities: {
    kind: "entities",
    defaultLabel: "Choose entities",
    requiresConfiguration: true,
    isApplicable: (block, answers) => block.entities.some((entity) => matchesAdapterAnswers(answers, entity.showWhen)),
    // Entity names fill later resources (mapping paths), so later steps wait for the required ones unless skippable.
    gate: (context) =>
      context.block.allowSkip || areBlockQuestionsAnswered({ block: { questions: getEntitySelectionQuestions(context.block) }, answers: context.answers })
        ? undefined
        : "Choose the required entities before continuing.",
    status: ({ block, answers }) => completeWhen(areBlockQuestionsAnswered({ block: { questions: getEntitySelectionQuestions(block) }, answers })),
    loadComponent: () => import("@/features/adapters/components/blocks/EntitiesBlock"),
  },
  questions: {
    kind: "questions",
    defaultLabel: "Answer the questions",
    requiresConfiguration: true,
    isApplicable: (block, answers) => block.questions.some((question) => matchesAdapterAnswers(answers, question.showWhen)),
    // Answers drive what the later steps contain, so they stay locked until the required ones are given.
    gate: (context) =>
      context.block.allowSkip || areBlockQuestionsAnswered(context)
        ? undefined
        : "Complete the setup questions before continuing.",
    status: (context) => completeWhen(areBlockQuestionsAnswered(context)),
    loadComponent: () => import("@/features/adapters/components/blocks/QuestionsBlock"),
  },
  exampleFile: {
    kind: "exampleFile",
    defaultLabel: "Example file",
    requiresConfiguration: false,
    // Optional: it only offers a file to download.
    status: () => undefined,
    loadComponent: () => import("@/features/adapters/components/blocks/ExampleFileBlock"),
  },
  scenarioFiles: {
    kind: "scenarioFiles",
    defaultLabel: "Scenario files",
    requiresConfiguration: false,
    // Optional: it only offers files to download.
    status: () => undefined,
    loadComponent: () => import("@/features/adapters/components/blocks/ScenarioFilesBlock"),
  },
  tenancySetting: {
    kind: "tenancySetting",
    defaultLabel: "Check tenancy settings",
    requiresConfiguration: true,
    resourceBacked: true,
    // Complete once every setting the step's blocks declare has the value the setup needs.
    status: resourceBlockStatus,
    loadComponent: () => import("@/features/adapters/components/blocks/TenancySettingBlock"),
  },
  contactEntity: {
    kind: "contactEntity",
    defaultLabel: "Set the contact entity",
    requiresConfiguration: true,
    resourceBacked: true,
    // Shown, but never holds the step up: it sits on the entities step, before the mapping that picks the contact ID.
    status: () => undefined,
    // Still left to do while the entity isn't the contact entity, so it's flagged once the setup moves past it.
    outstanding: (context) => blockInspections(context).some((inspection) => !inspection.complete),
    loadComponent: () => import("@/features/adapters/components/blocks/ContactEntityBlock"),
  },
  mapping: {
    kind: "mapping",
    defaultLabel: "Set up the mapping",
    requiresConfiguration: true,
    guidedFields: true,
    resourceBacked: true,
    status: resourceBlockStatus,
    loadComponent: () => import("@/features/adapters/components/blocks/MappingBlock"),
  },
  connection: {
    kind: "connection",
    defaultLabel: "Set up the connection",
    requiresConfiguration: true,
    guidedFields: true,
    resourceBacked: true,
    status: resourceBlockStatus,
    defaultContent: (step) =>
      getStepResources(step, "connection")[0]?.resource.stepContent ??
      "Enter the connection details, save them, then test the connection.",
    Header: ConnectionStepHeader,
    loadComponent: () => import("@/features/adapters/components/blocks/ConnectionBlock"),
  },
  connector: {
    kind: "connector",
    defaultLabel: "Set up event alerts",
    requiresConfiguration: true,
    resourceBacked: true,
    status: (context) => combineAdapterStatuses([
      resourceBlockStatus(context),
      ...(context.block.items ?? [])
        .filter((item) => matchesAdapterAnswers(context.answers, item.showWhen))
        .map((item) => completeWhen(Boolean(context.confirmations[item.id]))),
    ]),
    defaultContent: () => "Create an event connector, subscribe it to the required events, and test delivery.",
    Header: ConnectorStepHeader,
    loadComponent: () => import("@/features/adapters/components/blocks/ConnectorBlock"),
  },
  routing: {
    kind: "routing",
    defaultLabel: "Connect incoming files",
    requiresConfiguration: true,
    resourceBacked: true,
    status: resourceBlockStatus,
    defaultContent: () => "### Connect incoming files\n\nConnect each file type to the mapping that will process it.",
    loadComponent: () => import("@/features/adapters/components/blocks/RoutingBlock"),
  },
  fileProcessing: {
    kind: "fileProcessing",
    defaultLabel: "Enable file processing",
    requiresConfiguration: true,
    status: ({ project }) => completeWhen(getAdapterFileProcessingDetails(project).ready),
    defaultContent: () => "### Enable file processing\n\nEnable inbound SFTP for the project and review the folders used for incoming files.",
    loadComponent: () => import("@/features/adapters/components/blocks/FileProcessingBlock"),
  },
  trigger: {
    kind: "trigger",
    defaultLabel: "Set up delivery",
    requiresConfiguration: true,
    resourceBacked: true,
    status: resourceBlockStatus,
    defaultContent: (step) =>
      getStepResources(step, "delivery")[0]?.resource.stepContent ??
      "### Send completed results\n\nChoose the attribute that should start delivery when its result is ready.",
    loadComponent: () => import("@/features/adapters/components/blocks/TriggerBlock"),
  },
  verification: {
    kind: "verification",
    defaultLabel: "Check the integration",
    requiresConfiguration: true,
    status: ({ block, answers, confirmations }) =>
      progressOf(block.items.filter((item) => matchesAdapterAnswers(answers, item.showWhen)).map((item) => Boolean(confirmations[item.id]))),
    loadComponent: () => import("@/features/adapters/components/blocks/VerificationBlock"),
  },
  finish: {
    kind: "finish",
    defaultLabel: "Review and finish",
    requiresConfiguration: true,
    aggregate: true,
    status: (context) => completeWhen(
      areVerificationItemsConfirmed(context) &&
      context.steps.every(
        (step) =>
          step.key === context.step.key ||
          context.completion[step.key] ||
          // A resource-backed step with nothing applicable has no outstanding requirement.
          (getAdapterStepTraits(step).resourceBacked &&
            !context.inspections.some((inspection) => inspection.stepId === step.key && inspection.applicable)),
      ),
    ),
    defaultContent: () => "### Review the setup\n\nResolve anything marked not ready, then close the guide.",
    loadComponent: () => import("@/features/adapters/components/blocks/FinishBlock"),
  },
};

export const adapterBlockKinds = Object.keys(registrations) as AdapterStepBlockKind[];

/** Block kinds whose component is loaded on demand. */
export const adapterLazyBlockKinds = adapterBlockKinds.filter((kind) => Boolean(registrations[kind].loadComponent));

export const getAdapterBlockRegistration = (kind: AdapterStepBlockKind): AdapterBlockRegistration =>
  registrations[kind] as unknown as AdapterBlockRegistration;

type StepBlocks = Pick<AdapterWorkflowStepDefinition, "blocks">;

/** The step's main block: its first non-informational block (for example its questions or mapping). */
export const getStepMainBlock = (step: StepBlocks): AdapterStepBlock | undefined =>
  (step.blocks ?? []).find((block) => !getAdapterBlockRegistration(block.kind).informational);

/** A step with only informational blocks (for example an introduction): nothing to configure. */
export const isInformationalStep = (step: StepBlocks): boolean => !getStepMainBlock(step);

export interface AdapterStepTraits {
  defaultLabel: string;
  requiresConfiguration: boolean;
  aggregate: boolean;
  resourceBacked: boolean;
  guidedFields: boolean;
}

/** What a step is like, derived from its blocks. */
export const getAdapterStepTraits = (step: StepBlocks): AdapterStepTraits => {
  const blockRegistrations = (step.blocks ?? []).map((block) => getAdapterBlockRegistration(block.kind));
  const main = getStepMainBlock(step);
  return {
    defaultLabel: (main && getAdapterBlockRegistration(main.kind).defaultLabel) ?? "About this setup",
    requiresConfiguration: blockRegistrations.some((registration) => registration.requiresConfiguration),
    aggregate: blockRegistrations.some((registration) => Boolean(registration.aggregate)),
    resourceBacked: blockRegistrations.some((registration) => Boolean(registration.resourceBacked)),
    guidedFields: blockRegistrations.some((registration) => Boolean(registration.guidedFields)),
  };
};

/** Whether every block with an applicability rule applies to the current answers. */
export const areStepBlocksApplicable = (step: StepBlocks, answers: AdapterApplyAnswers): boolean =>
  getApplicableStepBlocks(step, answers).every((block) => {
    const isApplicable = getAdapterBlockRegistration(block.kind).isApplicable as
      | ((block: AdapterStepBlock, answers: AdapterApplyAnswers) => boolean)
      | undefined;
    return !isApplicable || isApplicable(block, answers);
  });

const blockContexts = (context: AdapterStepCompletionContext): AdapterBlockCompletionContext[] =>
  getApplicableStepBlocks(context.step, context.answers).map((block) => ({ ...context, block }));

/** A block's status, from its kind's rule; undefined when it has nothing to track. */
export const getAdapterBlockStatus = (context: AdapterBlockCompletionContext): AdapterBlockStatus | undefined =>
  getAdapterBlockRegistration(context.block.kind).status(context);

/** A step's status: its applicable blocks' combined. */
export const getAdapterStepStatus = (context: AdapterStepCompletionContext): AdapterBlockStatus =>
  combineAdapterStatuses(blockContexts(context).map(getAdapterBlockStatus));

/** Whether one of the step's blocks has something left to do that doesn't hold the step up. */
export const hasAdapterStepOutstandingItems = (context: AdapterStepCompletionContext): boolean =>
  blockContexts(context).some((blockContext) =>
    (getAdapterBlockRegistration(blockContext.block.kind).outstanding as
      | ((context: AdapterBlockCompletionContext) => boolean)
      | undefined)?.(blockContext) ?? false);

/** A step is complete when all of its applicable blocks are. */
export const isAdapterStepComplete = (context: AdapterStepCompletionContext): boolean =>
  getAdapterStepStatus(context) === "success";

/** The first reason one of the step's blocks keeps later steps locked. */
export const getAdapterStepGate = (context: AdapterStepCompletionContext): string | undefined => {
  for (const blockContext of blockContexts(context)) {
    const reason = getAdapterBlockRegistration(blockContext.block.kind).gate?.(blockContext);
    if (reason) return reason;
  }
  return undefined;
};

/** The step's main block's introduction, used when the step declares no content block. */
export const getAdapterStepDefaultContent = (step: AdapterWizardStep): string | undefined => {
  const main = getStepMainBlock(step);
  return main ? getAdapterBlockRegistration(main.kind).defaultContent?.(step, main) : undefined;
};

/** The step's main block's header, rendered above the step's blocks. */
export const getAdapterStepHeader = (
  step: StepBlocks,
): { block: AdapterStepBlock; Header: ComponentType<AdapterBlockProps> } | undefined => {
  const main = getStepMainBlock(step);
  const Header = main ? getAdapterBlockRegistration(main.kind).Header : undefined;
  return main && Header ? { block: main, Header } : undefined;
};

const lazyComponents = new Map<AdapterStepBlockKind, LazyExoticComponent<ComponentType<AdapterBlockProps>>>();

/**
 * The component for a block kind: eager for small shared blocks, lazily loaded for interactive blocks. One stable
 * component per kind so React keeps its state.
 */
export const getAdapterBlockComponent = (kind: AdapterStepBlockKind): ComponentType<AdapterBlockProps> | undefined => {
  const registration = getAdapterBlockRegistration(kind);
  if (registration.Component) return registration.Component;
  if (!registration.loadComponent) return undefined;
  let component = lazyComponents.get(kind);
  if (!component) {
    component = lazy(registration.loadComponent);
    lazyComponents.set(kind, component);
  }
  return component;
};
